import type { SupabaseClient } from "@supabase/supabase-js";

// Pending membership invites for the SIGNED-IN caller, and the hybrid accept decision built on
// top of them (product lock 2026-08-07, 1.4/F2 + design draft 1 plate 06 "Join account").
//
// Shape of the flow: a guard is invite-only (lock 1.5), so a session with zero memberships is not
// a dead end - it is the moment to resolve invitations. Zero pending -> honest dead end. Exactly
// one -> a single confirm screen naming the account (plate 06: "skip the picker entirely. The
// screen is a confirmation, not a decision."). Two or more -> a picker, newest pre-selected.
//
// SERVER DEPENDENCY: the caller-scoped list surface is Codex's DB slice
// (2026-08-07-prd-001-guard-invite-accept-db-parallel-brief.md), which may ship as an RPC or as a
// view. This module tries the RPC name first, then the view, and when NEITHER is deployed it
// returns status "unavailable" - never an empty list. Reporting "you have no invitations" because
// the server lacks the function would be a lie the guard cannot act on.
//
// Accept itself is already live: public.accept_account_member_invite (migration 20260611204135).

export interface PendingInvite {
  readonly inviteId: string;
  readonly accountId: string;
  readonly accountName: string;
  readonly role: string;
  readonly expiresAt: string | null;
  readonly createdAt: string | null;
}

/** Caller-scoped list, RPC form. Pinned here so mobile and Guard Web cannot drift apart. */
export const PENDING_INVITE_RPC = "list_my_pending_member_invites";

/** Caller-scoped list, view form (the DB brief allows either). */
export const PENDING_INVITE_VIEW = "my_pending_member_invites";

/**
 * Columns-safety allowlist - exactly the required projection from the DB brief, nothing more.
 * Optional server extras (inviter display, site) are deliberately NOT selected: asking for a
 * column the shipped view may not have would fail the whole read.
 */
export const PENDING_INVITE_COLUMNS = "invite_id,account_id,account_name,role,expires_at,created_at";

const PENDING_INVITE_LIMIT = 20;

/** The server has not deployed a caller-scoped invite list yet. Fail closed, say so plainly. */
export const PENDING_INVITES_UNAVAILABLE =
  "Invitations can't be checked on this server yet. Ask your SecOps admin to finish setup.";

/** Transport / permission failure - retryable, and never echoes the underlying error. */
export const PENDING_INVITES_FAILED = "Could not check for invitations. Check your connection and try again.";

export type PendingInviteLoad =
  | { readonly status: "ready"; readonly invites: readonly PendingInvite[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

// PostgREST/Postgres codes that mean "this surface does not exist here", as opposed to "the call
// failed". Anything else is treated as a real (retryable) failure.
const MISSING_SURFACE_CODES: ReadonlySet<string> = new Set([
  "42883", // undefined_function
  "42P01", // undefined_table
  "PGRST202", // function not found in the schema cache
  "PGRST205", // table/view not found in the schema cache
]);

/** True when the error says the RPC/view is simply not deployed on this project. */
export function isMissingInviteSurface(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && MISSING_SURFACE_CODES.has(code)) return true;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") return false;
  const lowered = message.toLowerCase();
  return (
    lowered.includes("could not find the function") ||
    lowered.includes("could not find the table") ||
    lowered.includes("does not exist")
  );
}

/** Parse one server row into a PendingInvite, or null when required fields are missing. */
export function normalizePendingInviteRow(raw: unknown): PendingInvite | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const inviteId = asText(row.invite_id);
  const accountId = asText(row.account_id);
  if (inviteId === null || accountId === null) return null;
  return {
    inviteId,
    accountId,
    accountName: asText(row.account_name) ?? "Workspace",
    role: asText(row.role) ?? "guard",
    expiresAt: asText(row.expires_at),
    createdAt: asText(row.created_at),
  };
}

/** Parse a server payload into invites, newest first. Unparseable rows are dropped, not guessed. */
export function normalizePendingInviteRows(raw: unknown): PendingInvite[] {
  if (!Array.isArray(raw)) return [];
  const invites: PendingInvite[] = [];
  for (const row of raw) {
    const invite = normalizePendingInviteRow(row);
    if (invite !== null) invites.push(invite);
  }
  return sortNewestFirst(invites);
}

/**
 * Pending invites for the signed-in caller. Tries the RPC form, then the view form; returns
 * "unavailable" only when both are absent from this project.
 */
export async function fetchPendingInvites(client: SupabaseClient): Promise<PendingInviteLoad> {
  try {
    const { data, error } = await client.rpc(PENDING_INVITE_RPC);
    if (error === null) {
      return { status: "ready", invites: normalizePendingInviteRows(data) };
    }
    if (!isMissingInviteSurface(error)) {
      return { status: "failed", reason: PENDING_INVITES_FAILED };
    }
  } catch {
    return { status: "failed", reason: PENDING_INVITES_FAILED };
  }

  // The RPC form is absent; the DB brief allows the view form instead.
  try {
    const { data, error } = await client
      .from(PENDING_INVITE_VIEW)
      .select(PENDING_INVITE_COLUMNS)
      .limit(PENDING_INVITE_LIMIT);
    if (error === null && Array.isArray(data)) {
      return { status: "ready", invites: normalizePendingInviteRows(data) };
    }
    if (error !== null && isMissingInviteSurface(error)) {
      return { status: "unavailable", reason: PENDING_INVITES_UNAVAILABLE };
    }
    return { status: "failed", reason: PENDING_INVITES_FAILED };
  } catch {
    return { status: "failed", reason: PENDING_INVITES_FAILED };
  }
}

export type PendingInviteDecision =
  | { readonly kind: "none" }
  | { readonly kind: "single"; readonly invite: PendingInvite }
  | {
      readonly kind: "choose";
      readonly invites: readonly PendingInvite[];
      readonly suggestedInviteId: string;
    };

/**
 * The hybrid accept decision (lock 1.4/F2, plate 06):
 *   []      -> nothing to join; the caller shows the honest no-workspace dead end
 *   [one]   -> no picker: one confirm screen naming that account
 *   [two+]  -> picker, newest invite pre-selected
 */
export function resolvePendingInviteDecision(invites: readonly PendingInvite[]): PendingInviteDecision {
  const ordered = sortNewestFirst([...invites]);
  if (ordered.length === 0) return { kind: "none" };
  if (ordered.length === 1) return { kind: "single", invite: ordered[0]! };
  return { kind: "choose", invites: ordered, suggestedInviteId: ordered[0]!.inviteId };
}

export const ACCEPT_INVITE_RPC = "accept_account_member_invite";

/** The one generic accept failure. Anything not on the allowlist collapses to this. */
export const SAFE_ACCEPT_FAILURE = "Couldn't join that workspace - please try again.";

/**
 * Contract-stable, value-free sentences raised by accept_account_member_invite
 * (migration 20260611204135). Anything else - including internal caller-mismatch guards - is
 * replaced by SAFE_ACCEPT_FAILURE so the surface cannot become an error oracle.
 */
export const KNOWN_ACCEPT_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to accept account member invites.",
  "Account id is required to accept account member invites.",
  "Authenticated user email is required to accept account member invites.",
  "Pending invite was not found for this email.",
  "Pending invite is expired.",
  "Active account membership already exists for this email.",
  "Account membership is suspended.",
]);

export function mapAcceptFailure(message: unknown): string {
  return typeof message === "string" && KNOWN_ACCEPT_FAILURES.has(message) ? message : SAFE_ACCEPT_FAILURE;
}

export type AcceptInviteOutcome =
  | { readonly ok: true; readonly accountId: string; readonly role: string | null }
  | { readonly ok: false; readonly reason: string };

/**
 * Accept the pending invite for the signed-in caller's email on `accountId`. The server matches
 * the invite by the authenticated email - the client never asserts who it is.
 */
export async function acceptPendingInvite(
  client: SupabaseClient,
  accountId: string,
): Promise<AcceptInviteOutcome> {
  const trimmed = accountId.trim();
  if (trimmed.length === 0) {
    return { ok: false, reason: SAFE_ACCEPT_FAILURE };
  }
  try {
    const { data, error } = await client.rpc(ACCEPT_INVITE_RPC, { p_account_id: trimmed });
    if (error !== null) {
      return { ok: false, reason: mapAcceptFailure((error as { message?: unknown }).message) };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    return { ok: true, accountId: trimmed, role: asText(row?.role) };
  } catch {
    return { ok: false, reason: SAFE_ACCEPT_FAILURE };
  }
}

function sortNewestFirst(invites: PendingInvite[]): PendingInvite[] {
  return invites.sort((left, right) => {
    const leftAt = Date.parse(left.createdAt ?? "");
    const rightAt = Date.parse(right.createdAt ?? "");
    const leftValid = Number.isFinite(leftAt);
    const rightValid = Number.isFinite(rightAt);
    if (leftValid && rightValid && leftAt !== rightAt) return rightAt - leftAt;
    if (leftValid !== rightValid) return leftValid ? -1 : 1;
    return left.accountName.localeCompare(right.accountName);
  });
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}
